/**
 * AuditViewer — Paginated audit log viewer for any entity.
 *
 * Calls GET /api/v1/audit-logs?entityName=X&entityId=Y
 */

import { useEffect, useState } from 'react';
import { ShieldCheck, ChevronLeft, ChevronRight, RefreshCcw } from 'lucide-react';
import { auditLogApi } from '@/api/attachment.api';
import type { AuditLogDto } from '@/types/attachment.types';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';

interface AuditViewerProps {
  entityName: string;
  entityId:   string;
  pageSize?:  number;
  className?: string;
}

const ACTION_COLORS: Record<string, string> = {
  CREATE:  'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  UPDATE:  'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  DELETE:  'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  POST:    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  SUBMIT:  'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  APPROVE: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  REJECT:  'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  REVERSE: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  CANCEL:  'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
};

const actionColor = (action: string) =>
  ACTION_COLORS[action.toUpperCase()] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400';

export const AuditViewer = ({
  entityName,
  entityId,
  pageSize = 15,
  className,
}: AuditViewerProps) => {
  const [logs,        setLogs]        = useState<AuditLogDto[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [page,        setPage]        = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);

  const load = async (p: number) => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await auditLogApi.getByEntity(entityName, entityId, p, pageSize);
      const d = res.data.data;
      setLogs(d?.items ?? []);
      setTotalCount(d?.totalCount ?? 0);
    } catch {
      setError('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); setPage(1); }, [entityName, entityId]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const goPage = (p: number) => {
    setPage(p);
    load(p);
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Audit Log
            {totalCount > 0 && (
              <span className="ml-1.5 text-xs font-normal text-slate-400">({totalCount})</span>
            )}
          </span>
        </div>
        <button
          onClick={() => load(page)}
          className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100
                     dark:hover:bg-slate-700 transition-colors"
          title="Refresh"
        >
          <RefreshCcw size={13} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
          <ShieldCheck size={32} className="mb-2 opacity-30" />
          <p className="text-sm">No audit entries found.</p>
        </div>
      ) : (
        <>
          {/* Log entries */}
          <div className="space-y-2">
            {logs.map(log => (
              <div
                key={log.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800
                           border border-slate-100 dark:border-slate-700"
              >
                {/* Action badge */}
                <span className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0 mt-0.5',
                  actionColor(log.action),
                )}>
                  {log.action}
                </span>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  {log.details && (
                    <p className="text-xs text-slate-600 dark:text-slate-300 break-words">
                      {log.details}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    {log.changedByName ?? log.changedBy}
                    {' · '}
                    {new Date(log.changedDate).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-slate-500 dark:text-gray-400">
                Page {page} of {totalPages} ({totalCount} entries)
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goPage(page - 1)}
                  disabled={page <= 1}
                  className="p-1.5 rounded text-slate-500 dark:text-gray-400 hover:text-slate-700 hover:bg-slate-100
                             dark:hover:bg-slate-700 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => goPage(page + 1)}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded text-slate-500 dark:text-gray-400 hover:text-slate-700 hover:bg-slate-100
                             dark:hover:bg-slate-700 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
